import { useFrame, useThree } from "@react-three/fiber"
import { Grid } from "@react-three/drei"
import { Bloom, EffectComposer, Noise, Vignette } from "@react-three/postprocessing"
import { useRef, type MutableRefObject } from "react"
import type { Group } from "three"
import type { LandingData } from "../data/types"
import type { DeviceTier } from "../lib/device-tier"
import { buildLandingMotion, sectionFocus, type LandingMotionRef, type LandingMotionState } from "../lib/landing-motion"
import { damp } from "../lib/three"
import { Globe } from "./Globe"
import { TickerStream } from "./TickerStream"
import { SignalLattice } from "./SignalLattice"
import { YieldTerrain } from "./YieldTerrain"
import { AtlasExplainer } from "./AtlasExplainer"
import { DashboardTour } from "./DashboardTour"
import { StatsCounters } from "./StatsCounters"
import { Particles } from "./Particles"

interface LandingSceneProps {
  data: LandingData
  motion: LandingMotionState
  tier: DeviceTier
}

export function LandingScene({ data, motion, tier }: LandingSceneProps) {
  const rig = useRef<Group>(null)
  const pointer = useRef({ x: 0, y: 0 })
  const smooth = useRef(0)
  const rawMotion = useRef(motion)
  const sceneMotion = useRef(buildLandingMotion(0))
  const { camera, gl } = useThree()
  rawMotion.current = motion

  useFrame((state) => {
    const velocityLift = Math.min(0.08, Math.abs(rawMotion.current.velocity) * 58)
    smooth.current = damp(smooth.current, rawMotion.current.progress, 0.068 + velocityLift)
    sceneMotion.current = buildLandingMotion(smooth.current, rawMotion.current.velocity, rawMotion.current.direction)
    const section = sceneMotion.current.section
    const hero = sectionFocus(section, 0, 1.05)
    const lattice = sectionFocus(section, 2, 1.05)
    const terrain = sectionFocus(section, 3, 1.05)
    const terminal = sectionFocus(section, 5, 1.05)

    camera.position.x = Math.sin(section * 0.58) * 0.34 + pointer.current.x * 0.06
    camera.position.y = 0.08 + terrain * 0.14 - lattice * 0.08 - pointer.current.y * 0.035
    camera.position.z = 7.35 - hero * 0.42 - terminal * 0.3 + Math.sin(section * 1.1) * 0.08
    camera.lookAt(Math.sin(section * 0.36) * 0.18, -0.08 + terrain * 0.12, 0)
    gl.domElement.dataset.section = String(Math.round(section))
    if (rig.current) {
      rig.current.rotation.y = pointer.current.x * 0.035 + Math.sin(section * 0.28) * 0.055
      rig.current.position.z = Math.sin(state.clock.elapsedTime * 0.16) * 0.035
    }
  })

  return (
    <>
      <ambientLight intensity={0.32} color="#6fffe8" />
      <pointLight position={[2.5, 2, 4]} intensity={1.8} color="#14d8b4" />
      <fog attach="fog" args={["#020404", 6.8, tier === "high" ? 15.5 : 13.5]} />
      <PointerPlane pointer={pointer} />
      <Particles count={tier === "high" ? 820 : 520} radius={14} />
      <group ref={rig}>
        <Globe data={data} motion={sceneMotion as LandingMotionRef} />
        <TickerStream data={data} motion={sceneMotion as LandingMotionRef} />
        <SignalLattice data={data} motion={sceneMotion as LandingMotionRef} />
        <YieldTerrain data={data} motion={sceneMotion as LandingMotionRef} />
        <AtlasExplainer data={data} motion={sceneMotion as LandingMotionRef} />
        <DashboardTour motion={sceneMotion as LandingMotionRef} />
        <StatsCounters data={data} motion={sceneMotion as LandingMotionRef} />
      </group>
      <Grid
        position={[0, -1.75, 0]}
        args={[30, 30]}
        cellColor="#13211e"
        sectionColor="#14d8b4"
        fadeDistance={tier === "high" ? 20 : 16}
        fadeStrength={0.9}
        infiniteGrid
      />
      {tier === "high" ? (
        <EffectComposer multisampling={2}>
          <Bloom intensity={0.16} luminanceThreshold={0.48} luminanceSmoothing={0.84} mipmapBlur />
          <Vignette eskil={false} offset={0.18} darkness={0.58} />
          <Noise opacity={0.026} premultiply />
        </EffectComposer>
      ) : null}
    </>
  )
}

function PointerPlane({ pointer }: { pointer: MutableRefObject<{ x: number; y: number }> }) {
  useFrame(({ pointer: p }) => {
    pointer.current.x = p.x
    pointer.current.y = p.y
  })
  return null
}
